import {useEffect, useState} from 'react';

/**
 * Provides a global, subscription-based mechanism to detect if the user is currently navigating via keyboard.
 *
 * This module manages a single global state (keyboardMode) that tracks whether the user is interacting with the UI via keyboard (vs. mouse/touch).
 *
 * - The state is managed outside of React context to avoid unnecessary re-renders and potential focus loss.
 * - Components can access this state using the useKeyboardMode() hook, or subscribe directly for non-React consumers.
 * - Global event listeners are added/removed automatically based on active subscribers.
 *
 * @module useKeyboardMode
 */

type KeyboardModeListener = (value: boolean) => void;

let keyboardMode = false;
const listeners = new Set<KeyboardModeListener>();

// Track the number of subscribers to manage event listeners
let subscriberCount = 0;

// Store references to event handlers so they can be removed
let listenersAdded = false;

const keyboardKeys = [
  // UI Navigation keys
  '/', // Opens keyboard tips
  '?', // Opens keyboard tips
  'Tab',
  'Shift',
  'Enter',
  ' ',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  // Project navigation keys:
  'Home', // Move focus to the first page of a project
  'End', // Move focus to the last page of a project
  'PageUp', // Move focus to the previous page of a project
  'PageDown', // Move focus to the next page of a project
  'Escape', // Close a dialog
  // Palette action keys
  '+', // Apply layout to all pages
  '=', // Apply layout to all pages
  '1', // Add or remove picture to box 1
  '2', // Add or remove picture to box 2
  '3', // Add or remove picture to box 3
  '4', // Add or remove picture to box 4
  '5', // Add or remove picture to box 5
  '6', // Add or remove picture to box 6
  '7', // Add or remove picture to box 7
  '8', // Add or remove picture to box 8
  '9', // Add or remove picture to box 9
  '0', // Add or remove picture to box 10
  // Spread side selection keys:
  '<', // Select left side of a spread
  '>', // Select right side of a spread
  ',', // Select left side of a spread
  '.', // Select right side of a spread
];

function onKeyDown(event: KeyboardEvent) {
  // Trigger keyboard mode unless this event was a non-modified arrow key when focused on a page (used for moving page items around)
  const isArrowKeyOnPage =
    !event.shiftKey &&
    !event.ctrlKey &&
    ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key) &&
    (event.target as HTMLElement).hasAttribute('data-tpx-page');

  if (keyboardKeys.includes(event.key) && !isArrowKeyOnPage) {
    setKeyboardMode(true);
  }
}

function onMouseDown(event: MouseEvent) {
  // Turn off keyboard when using a mouse, unless the click is on the keyboard tips button
  if (event.target instanceof HTMLElement && !event.target.closest('#keyboard-tips-button')) {
    setKeyboardMode(false);
  }
}

function onTouchStart(event: TouchEvent) {
  // Turn off keyboard when using a touch, unless the click is on the keyboard tips button
  if (event.target instanceof HTMLElement && !event.target.closest('#keyboard-tips-button')) {
    setKeyboardMode(false);
  }
}

function addGlobalListeners() {
  if (!listenersAdded) {
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('touchstart', onTouchStart);
    listenersAdded = true;
  }
}

function removeGlobalListeners() {
  if (listenersAdded) {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('mousedown', onMouseDown);
    window.removeEventListener('touchstart', onTouchStart);
    listenersAdded = false;
  }
}

/**
 * Returns the current value of keyboard mode (true if the user is navigating via keyboard).
 *
 * @returns {boolean} Whether keyboard mode is currently active.
 */
export function getKeyboardMode() {
  return keyboardMode;
}

/**
 * Sets the global keyboard mode state and notifies all subscribers if the value changes.
 *
 * @param {boolean} value - The new keyboard mode state.
 */
export function setKeyboardMode(value: boolean) {
  if (keyboardMode !== value) {
    keyboardMode = value;
    listeners.forEach(listener => listener(keyboardMode));
  }
}

/**
 * Subscribes a listener to keyboard mode changes. The listener is called immediately with the current value, and on every change.
 *
 * Global event listeners for keyboard/mouse/touch are managed automatically based on active subscribers.
 *
 * @param {KeyboardModeListener} listener - Function to call with the current keyboard mode value.
 * @returns {() => void} Unsubscribe function to remove the listener.
 */
export function subscribeKeyboardMode(listener: KeyboardModeListener) {
  listeners.add(listener);
  listener(keyboardMode);
  subscriberCount++;
  if (subscriberCount === 1) {
    addGlobalListeners();
  }
  return () => {
    listeners.delete(listener);
    subscriberCount--;
    if (subscriberCount === 0) {
      removeGlobalListeners();
    }
  };
}

/**
 * React hook that provides the current keyboard mode state (true if the user is navigating via keyboard).
 *
 * This hook subscribes to global keyboard mode changes and updates the component when the input method changes.
 *
 * @returns {boolean} Whether keyboard mode is currently active.
 */
export default function useKeyboardMode() {
  const [mode, setMode] = useState(false);

  useEffect(() => {
    return subscribeKeyboardMode(setMode);
  }, []);

  return mode;
}
